import { useCallback } from 'react';
import toast from 'react-hot-toast';
import type { UpdateScheduleEntryDto } from '@myapp/contract';
import { useCreateAppointment } from '../../../entities/appointment';
import { useUpdateScheduleEntry } from '../../../entities/schedule';
import {
  useCreateTask,
  useScheduleTask,
  useTaskDetail,
  useUpdateTask,
} from '../../../entities/task';
import { fromDatetimeLocal } from '../../../shared/helpers/date';
import { getErrorMessage } from '../../../shared/helpers/getErrorMessage';
import { useScheduleConflictFlow } from '../../../shared/hooks/useScheduleConflictFlow';
import { overrideFields, type ScheduleOverrides } from '../../../shared/lib/schedule';
import { draftToDefaultSlot, type PlanningDraftState } from './draft';
import type { DispositionMode } from './useDispositionMode';
import type { RescheduleSource } from './useRescheduleSource';

interface Args {
  plan: DispositionMode;
  /** The Termin / Aufgabe being moved, null for a placement. */
  source: RescheduleSource | null;
  draft: PlanningDraftState | null;
  dayIso: string;
  /** Title for a brand-new Aufgabe — the one field `POST /tasks` needs that the board cannot supply. */
  title: string;
  /** Called after the request landed — the board uses it to drop out of planning mode. */
  onSaved: () => void;
}

const SUCCESS_TEXT = {
  appointmentCreate: 'Termin angelegt',
  appointmentMove: 'Termin verschoben',
  taskCreate: 'Aufgabe angelegt',
  taskPlan: 'Aufgabe geplant',
  taskMove: 'Aufgabe verschoben',
} as const;

/**
 * Saves the draft exactly as the board shows it — the "no form" half of the planning bar.
 *
 * The form route stays: the bar's second button still opens the docked panel prefilled from the
 * same draft. This hook only exists so the common case (everything was already said on the board)
 * costs one click instead of two.
 *
 * Requests mirror what the forms and the drag already send, so the server cannot tell the three
 * apart: a create is `POST /appointments` / `POST /tasks`, a first placement is the modal's
 * `PATCH /tasks/:id` (technician only) → `PATCH /tasks/:id/schedule`, and a move is the drag's
 * `PATCH /schedule/:entryId`.
 */
export function useCommitPlan({
  plan,
  source,
  draft,
  dayIso,
  title,
  onSaved,
}: Args) {
  const conflictFlow = useScheduleConflictFlow();

  // Same query the bar already runs — React Query dedupes it.
  const task = useTaskDetail(plan.taskId ?? undefined);

  const createAppointment = useCreateAppointment();
  const updateEntry = useUpdateScheduleEntry();
  const createTask = useCreateTask();
  // Bound to an id at call time; `0` is never used — every path that reads these has a task id.
  const updateTask = useUpdateTask(plan.taskId ?? 0);
  const scheduleTask = useScheduleTask(plan.taskId ?? 0);

  const isPending =
    createAppointment.isPending ||
    updateEntry.isPending ||
    createTask.isPending ||
    updateTask.isPending ||
    scheduleTask.isPending;

  const isAppointment = plan.mode === 'schedule-appointment';
  const isMove = source != null;
  const isNewTask = plan.mode === 'schedule-task' && plan.taskId == null;

  const canCommit = (() => {
    if (!draft || isPending) return false;
    if (isMove) return source.entryId != null && !source.isLocked;
    if (isAppointment) return true;
    if (isNewTask) return title.trim().length > 0;
    return task.data != null;
  })();

  const commit = useCallback(
    async (overrides: ScheduleOverrides = {}) => {
      if (!draft) return;

      // The single draft → form conversion; the Anfahrt arithmetic lives there and nowhere else.
      const slot = draftToDefaultSlot(draft, dayIso);
      const plannedStart = fromDatetimeLocal(slot.plannedStart);
      if (!plannedStart) return;

      const durations = {
        plannedTravelDuration: draft.durations.travel,
        plannedWorkDuration: draft.durations.work,
        plannedDepartureDuration: draft.durations.departure,
      };

      try {
        let message: string;

        if (source) {
          // A move: the drag's request, addressed by `schedule.id` — not by the owner's id.
          const dto: UpdateScheduleEntryDto = {
            slot: {
              kind: 'SEGMENTED',
              workStartsAt: plannedStart,
              travelMinutes: draft.durations.travel,
              workMinutes: draft.durations.work,
              departureMinutes: draft.durations.departure,
            },
            // Reshaping the block means it no longer fills the Schicht — same rule as the drag.
            ...(source.kind === 'appointment' ? { allDay: false } : {}),
            ...(draft.technicianId !== source.seed.state.technicianId
              ? { technicianId: draft.technicianId }
              : {}),
            ...overrideFields(overrides),
          };
          await updateEntry.mutateAsync({ entryId: source.entryId as number, dto });
          message = isAppointment ? SUCCESS_TEXT.appointmentMove : SUCCESS_TEXT.taskMove;
        } else if (isAppointment) {
          await createAppointment.mutateAsync({
            workOrderId: plan.workOrderId as string,
            assignedTechnicianId: draft.technicianId,
            plannedStart,
            ...durations,
            attachments: null,
            ...overrideFields(overrides),
          });
          message = SUCCESS_TEXT.appointmentCreate;
        } else if (isNewTask) {
          await createTask.mutateAsync({
            title: title.trim(),
            taskType: 'GENERAL_TASK',
            assignedTechnicianId: draft.technicianId,
            plannedStart,
            ...durations,
            ...overrideFields(overrides),
          });
          message = SUCCESS_TEXT.taskCreate;
        } else {
          // First Termin for an existing Aufgabe. `/schedule` takes no technician, so a different
          // row than the one it already has is saved first — the modal's two-call order.
          if (task.data?.assignedTechnicianId !== draft.technicianId) {
            await updateTask.mutateAsync({ assignedTechnicianId: draft.technicianId });
          }
          await scheduleTask.mutateAsync({
            plannedStart,
            ...durations,
            ...overrideFields(overrides),
          });
          message = SUCCESS_TEXT.taskPlan;
        }

        toast.success(message);
        onSaved();
      } catch (error) {
        // A 409 opens the matching dialog, whose confirmation re-runs this with one more override.
        const handled = conflictFlow.handle(error, {
          overrides,
          attempt: (next) => commit(next),
        });
        if (!handled) toast.error(getErrorMessage(error));
      }
    },
    [
      draft,
      dayIso,
      source,
      isAppointment,
      isNewTask,
      plan.workOrderId,
      title,
      task.data,
      conflictFlow,
      updateEntry,
      createAppointment,
      createTask,
      updateTask,
      scheduleTask,
      onSaved,
    ],
  );

  return { commit: () => commit(), canCommit, isPending, conflictFlow };
}
